import React, { useMemo, useState } from "react";
import { MatchState, Delivery, Player, Inning } from "../matchTypes";
import { getRequiredRunRate, getCurrentRunRate } from "../engine/MatchEngine";
import { getBowlerStats } from "../engine/playerStats";
import { formatPlayerName } from "../utils/nameFormatter";
import { derivePartnerships, Partnership } from "../utils/partnerships";
import TeamLogo from "../components/broadcast/TeamLogo";
import ScoreComparisonGraph from "../components/ScoreComparisonGraph";

type Props = { state: MatchState };

type Section = "scorecard" | "commentary" | "overs" | "partnerships" | "graphs";

type BowlingRow = {
  id: string;
  name: string;
  balls: number;
  runs: number;
  wickets: number;
  wides: number;
  noballs: number;
  isCaptain?: boolean;
  isKeeper?: boolean;
  oversText: string;
  econ: string;
};

function ballsToOvers(balls: number, ballsPerOver: number): string {
  const overs = Math.floor(balls / ballsPerOver);
  const remainingBalls = balls % ballsPerOver;
  return remainingBalls ? `${overs}.${remainingBalls}` : `${overs}`;
}

function formatTeamFormat(format: MatchState["format"]): string {
  return format === "INDOOR" ? "INDOOR" : format;
}

function formatStatus(state: MatchState, currentInning: Inning | undefined): string {
  if (state.status === "completed") return "COMPLETED";
  if (currentInning?.completed) return "INNINGS BREAK";
  return state.currentInnings > 0 ? "2ND INNINGS" : "1ST INNINGS";
}

function scoreText(inning: Inning | undefined): string {
  return inning ? `${inning.totalRuns}/${inning.wickets}` : "—";
}

function teamName(state: MatchState, team: "A" | "B"): string {
  return team === "A" ? state.teamAName : state.teamBName;
}

function teamLogo(state: MatchState, team: "A" | "B"): string | null | undefined {
  return team === "A" ? state.teamALogo : state.teamBLogo;
}

function InningsCard({
  inning,
  state,
  selected,
  onClick,
  index,
}: {
  inning: Inning;
  state: MatchState;
  selected: boolean;
  onClick: () => void;
  index: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`scorecard-innings-tab ${selected ? "scorecard-innings-tab-active" : ""}`}
      aria-pressed={selected}
    >
      <span>{index === 0 ? "1st Innings" : "2nd Innings"}</span>
      <small>{teamName(state, inning.battingTeam)}</small>
      <strong>{scoreText(inning)}</strong>
    </button>
  );
}

function TeamScore({ state, team, inning }: { state: MatchState; team: "A" | "B"; inning?: Inning }) {
  return (
    <div className="scorecard-team-score">
      <TeamLogo logoUrl={teamLogo(state, team)} teamName={teamName(state, team)} size="lg" />
      <div className="scorecard-team-copy">
        <span className="scorecard-team-name">{teamName(state, team)}</span>
        <strong>{scoreText(inning)}</strong>
        <small>({inning ? ballsToOvers(inning.totalBalls, state.ballsPerOver || 6) : "0"} overs)</small>
      </div>
    </div>
  );
}

// ---- Commentary section (match-level, same as existing Commentary tab) ----
function CommentarySection({ state }: { state: MatchState }) {
  const [filter, setFilter] = useState<"all" | "fours" | "sixes" | "wickets">("all");

  const allComments = useMemo(() => {
    const chronological = [...state.commentary].reverse();
    return chronological.filter((comment) => {
      if (filter === "all") return true;
      const text = comment.text.toLowerCase();
      if (filter === "fours") return text.includes("four") || text.includes("boundary");
      if (filter === "sixes") return text.includes("six");
      if (filter === "wickets") return text.includes("wicket") || text.includes("out");
      return true;
    });
  }, [state.commentary, filter]);

  return (
    <section className="scorecard-section-panel">
      <div className="scorecard-section-header">
        <h2>Commentary</h2>
        <span className="scorecard-section-count">{allComments.length} {allComments.length === 1 ? "entry" : "entries"}</span>
      </div>
      <div className="scorecard-filter-bar">
        {(["all", "fours", "sixes", "wickets"] as const).map((f) => (
          <button
            key={f}
            type="button"
            className={`scorecard-filter-btn ${filter === f ? "scorecard-filter-active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? "All" : f === "fours" ? "4s" : f === "sixes" ? "6s" : "Wkts"}
          </button>
        ))}
      </div>
      <div className="scorecard-commentary-note">Match commentary — not separated by innings.</div>
      {allComments.length === 0 ? (
        <div className="scorecard-no-data-panel">No commentary yet.</div>
      ) : (
        <div className="scorecard-commentary-list">
          {allComments.map((comment, idx) => (
            <div key={idx} className={`scorecard-commentary-item ${comment.auto ? "" : "scorecard-commentary-manual"}`}>
              <span className="scorecard-commentary-over">{comment.over}.{comment.ball}</span>
              <span className="scorecard-commentary-text">{comment.text}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---- Overs section ----
function OversSection({ state, inn }: { state: MatchState; inn: Inning }) {
  const bpo = state.ballsPerOver || 6;
  const battingPlayers = inn.battingTeam === "A" ? state.teamAPlayers : state.teamBPlayers;
  const bowlingPlayers = inn.battingTeam === "A" ? state.teamBPlayers : state.teamAPlayers;

  const overs = useMemo(() => {
    const result: { overNumber: number; deliveries: Delivery[]; totalRuns: number; wickets: number }[] = [];
    let currentOver: Delivery[] = [];
    let legalBalls = 0;
    let overNumber = 0;
    let overRuns = 0;
    let overWickets = 0;

    inn.deliveries.forEach((delivery) => {
      currentOver.push(delivery);
      overRuns += delivery.runs;
      if (delivery.isWicket) overWickets++;
      if (delivery.isLegal) {
        legalBalls++;
        if (legalBalls % bpo === 0) {
          result.push({ overNumber: overNumber + 1, deliveries: [...currentOver], totalRuns: overRuns, wickets: overWickets });
          currentOver = [];
          overRuns = 0;
          overWickets = 0;
          overNumber++;
        }
      }
    });

    if (currentOver.length > 0) {
      result.push({ overNumber: overNumber + 1, deliveries: [...currentOver], totalRuns: overRuns, wickets: overWickets });
    }

    return result;
  }, [inn.deliveries, bpo]);

  const playerName = (id?: string) => {
    const p = battingPlayers.find((pl) => pl.id === id);
    return p ? formatPlayerName(p.name) : "—";
  };
  const bowlerName = (id?: string) => {
    const p = bowlingPlayers.find((pl) => pl.id === id);
    return p ? formatPlayerName(p.name) : "—";
  };

  return (
    <section className="scorecard-section-panel">
      <div className="scorecard-section-header">
        <h2>Overs</h2>
        <span className="scorecard-section-count">{overs.length} {overs.length === 1 ? "over" : "overs"}</span>
      </div>
      {overs.length === 0 ? (
        <div className="scorecard-no-data-panel">No overs bowled yet in this innings.</div>
      ) : (
        <div className="scorecard-overs-list">
          {overs.map((over) => (
            <div key={over.overNumber} className="scorecard-over-card">
              <div className="scorecard-over-header">
                <span className="scorecard-over-number">Over {over.overNumber}</span>
                <span className="scorecard-over-total">{over.totalRuns} runs{over.wickets > 0 ? ` · ${over.wickets} wkt${over.wickets > 1 ? "s" : ""}` : ""}</span>
              </div>
              <div className="scorecard-over-balls">
                {over.deliveries.map((delivery, idx) => (
                  <span key={idx} className={`scorecard-over-ball ${delivery.isWicket ? "scorecard-ball-wicket" : ""} ${!delivery.isLegal ? "scorecard-ball-extra" : ""} ${delivery.runs === 4 ? "scorecard-ball-four" : ""} ${delivery.runs === 6 ? "scorecard-ball-six" : ""}`}>
                    {delivery.display}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---- Partnerships section ----
function PartnershipsSection({ state, inn }: { state: MatchState; inn: Inning }) {
  const bpo = state.ballsPerOver || 6;
  const battingPlayers = inn.battingTeam === "A" ? state.teamAPlayers : state.teamBPlayers;

  const partnerships = useMemo(() => derivePartnerships(inn, bpo), [inn, bpo]);

  const playerName = (id: string) => {
    const p = battingPlayers.find((pl) => pl.id === id);
    return p ? formatPlayerName(p.name) : "—";
  };

  return (
    <section className="scorecard-section-panel">
      <div className="scorecard-section-header">
        <h2>Partnerships</h2>
        <span className="scorecard-section-count">{partnerships.length}</span>
      </div>
      {partnerships.length === 0 ? (
        <div className="scorecard-no-data-panel">No partnerships yet.</div>
      ) : (
        <>
        <div className="scorecard-partnership-note">Partnerships are derived from the delivery history. Retire/re-entry events are not recorded in the delivery log, so a partnership may appear continuous across a retirement boundary.</div>
        <div className="scorecard-partnerships-list">
          {partnerships.map((p) => (
            <div key={p.wicketNumber} className={`scorecard-partnership-card ${p.isCurrent ? "scorecard-partnership-current" : ""}`}>
              <div className="scorecard-partnership-label">
                {p.endedByWicket ? `${ordinal(p.wicketNumber)} Wicket` : p.isCurrent ? "Current Partnership" : `${ordinal(p.wicketNumber)} Wicket`}
                {p.isCurrent && <span className="scorecard-current-badge">CURRENT</span>}
              </div>
              <div className="scorecard-partnership-batters">
                <div className="scorecard-partnership-batter">
                  <span className="scorecard-partnership-name">{playerName(p.batter1Id)}</span>
                  <span className="scorecard-partnership-score">{p.batter1Runs} ({p.batter1Balls})</span>
                </div>
                <span className="scorecard-partnership-plus">+</span>
                <div className="scorecard-partnership-batter">
                  <span className="scorecard-partnership-name">{playerName(p.batter2Id)}</span>
                  <span className="scorecard-partnership-score">{p.batter2Runs} ({p.batter2Balls})</span>
                </div>
              </div>
              <div className="scorecard-partnership-total">
                {p.runs} runs · {p.balls} balls
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </section>
  );
}

function ordinal(n: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
}

// ---- Graphs section ----
function GraphsSection({ state }: { state: MatchState }) {
  return (
    <section className="scorecard-section-panel">
      <div className="scorecard-section-header">
        <h2>Score Progression</h2>
      </div>
      <div className="scorecard-graph-container">
        <ScoreComparisonGraph state={state} />
      </div>
    </section>
  );
}

export default function Scorecard({ state }: Props) {
  const startedInnings = useMemo(
    () => state.innings.filter((inning) => inning.totalBalls > 0 || inning.totalRuns > 0 || inning.deliveries.length > 0),
    [state.innings]
  );
  const [selectedInningsIndex, setSelectedInningsIndex] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState<Section>("scorecard");
  const currentInningsIndex = Math.min(state.currentInnings, Math.max(0, startedInnings.length - 1));
  const activeInningsIndex = selectedInningsIndex === null ? currentInningsIndex : Math.min(selectedInningsIndex, startedInnings.length - 1);

  if (startedInnings.length === 0) {
    return (
      <div className="scorecard-page scorecard-empty">
        <div className="scorecard-empty-card">
          <span className="scorecard-eyebrow">SCORECARD</span>
          <h1>No innings started</h1>
          <p>Start scoring to see the match card.</p>
        </div>
      </div>
    );
  }

  const inn = startedInnings[activeInningsIndex];
  const inningsStateIndex = state.innings.indexOf(inn);
  const firstInnings = startedInnings[0];
  const secondInnings = startedInnings[1];
  const bpo = state.ballsPerOver || 6;
  const isIndoor = state.format === "INDOOR";
  const isCurrentInnings = inningsStateIndex === state.currentInnings;
  const battingPlayers = inn.battingTeam === "A" ? state.teamAPlayers : state.teamBPlayers;
  const bowlingPlayers = inn.battingTeam === "A" ? state.teamBPlayers : state.teamAPlayers;
  const currentInning = state.innings[state.currentInnings];
  const isCompleted = state.status === "completed";
  // Authoritative target source: inn.target set by the engine during innings transition
  const target = secondInnings?.target ?? null;
  const runsRequired = target === null || !secondInnings ? null : Math.max(0, target - secondInnings.totalRuns);
  const ballsRemaining = secondInnings?.oversLimit == null || !secondInnings
    ? null
    : Math.max(0, secondInnings.oversLimit * bpo - secondInnings.totalBalls);
  // Authoritative RRR from engine
  const rrr = secondInnings ? getRequiredRunRate(secondInnings, bpo) : null;
  // Authoritative CRR from engine
  const crr = getCurrentRunRate(inn, bpo);
  const status = formatStatus(state, currentInning);

  // Authoritative bowling figures from engine helper
  const bowlerStatsMap = useMemo(() => getBowlerStats(inn, bowlingPlayers), [inn, bowlingPlayers]);
  const bowling = useMemo<BowlingRow[]>(() => {
    const rows: BowlingRow[] = [];
    const bowlingTeamPlayers = inn.battingTeam === "A" ? state.teamBPlayers : state.teamAPlayers;
    for (const [id, stats] of bowlerStatsMap.entries()) {
      if (stats.balls === 0 && stats.runsConceded === 0 && stats.wickets === 0) continue;
      const player = bowlingTeamPlayers.find((p) => p.id === id);
      rows.push({
        id,
        name: player?.name || "—",
        balls: stats.balls,
        runs: stats.runsConceded,
        wickets: stats.wickets,
        wides: stats.wides,
        noballs: stats.noballs,
        isCaptain: player?.isCaptain,
        isKeeper: player?.isKeeper,
        oversText: ballsToOvers(stats.balls, bpo),
        econ: stats.balls ? (stats.runsConceded / (stats.balls / bpo)).toFixed(2) : "—",
      });
    }
    return rows;
  }, [bowlerStatsMap, inn.battingTeam, state.teamAPlayers, state.teamBPlayers, bpo]);

  const batting = useMemo(() => battingPlayers.map((player: Player) => {
    let statusText = player.isOut ? player.dismissal || "out" : "not out";
    if (isIndoor && (player.outsCount || 0) > 0 && !player.isOut) statusText = `not out (re-entered ×${player.outsCount})`;
    const isStriker = isCurrentInnings && player.id === state.strikerId;
    const isNonStriker = isCurrentInnings && player.id === state.nonStrikerId;
    return {
      ...player,
      statusText,
      isStriker,
      isNonStriker,
      strikeRate: player.balls ? ((player.runs / player.balls) * 100).toFixed(1) : "—",
    };
  }), [battingPlayers, isCurrentInnings, isIndoor, state.nonStrikerId, state.strikerId]);

  const battersWhoFacedDeliveries = useMemo(() => {
    const ids = new Set<string>();
    for (const d of inn.deliveries) {
      if (d.batterId) ids.add(d.batterId);
    }
    return ids;
  }, [inn.deliveries]);

  const yetToBat = battingPlayers.filter((player) =>
    player.runs === 0 &&
    player.balls === 0 &&
    !player.isOut &&
    player.id !== state.strikerId &&
    player.id !== state.nonStrikerId &&
    !battersWhoFacedDeliveries.has(player.id)
  );
  const extras = inn.extras.wides + inn.extras.noBalls + inn.extras.byes + inn.extras.legByes;
  const extrasDetail = `Wd ${inn.extras.wides}, Nb ${inn.extras.noBalls}, B ${inn.extras.byes}, Lb ${inn.extras.legByes}`;

  const sections: { key: Section; label: string }[] = [
    { key: "scorecard", label: "Scorecard" },
    { key: "commentary", label: "Commentary" },
    { key: "overs", label: "Overs" },
    { key: "partnerships", label: "Partnerships" },
    { key: "graphs", label: "Graphs" },
  ];

  return (
    <div className="scorecard-page">
      <section className="scorecard-hero">
        <div className="scorecard-hero-topline">
          <span className="scorecard-eyebrow">MATCH CARD</span>
          <span className="scorecard-format">{formatTeamFormat(state.format)}</span>
          <span className={`scorecard-status ${isCompleted ? "scorecard-status-complete" : ""}`}>
            {!isCompleted && <i aria-hidden="true" />}{status}
          </span>
        </div>
        <div className="scorecard-matchup">
          <TeamScore state={state} team="A" inning={state.innings.find((inning) => inning.battingTeam === "A")} />
          <span className="scorecard-vs">VS</span>
          <TeamScore state={state} team="B" inning={state.innings.find((inning) => inning.battingTeam === "B")} />
        </div>
        {isCompleted ? (
          <div className="scorecard-result">{secondInnings?.result || firstInnings?.result || "Match completed"}</div>
        ) : secondInnings ? (
          <div className="scorecard-chase">
            <strong>TARGET {target}</strong>
            <span>Need {runsRequired} runs from {ballsRemaining ?? "—"} balls <b>•</b> RRR {rrr === null ? "—" : rrr.toFixed(2)}</span>
          </div>
        ) : (
          <div className="scorecard-first-innings">First innings in progress</div>
        )}
      </section>

      <nav className="scorecard-section-nav" aria-label="Scorecard sections">
        {sections.map((s) => (
          <button
            type="button"
            key={s.key}
            className={activeSection === s.key ? "scorecard-section-active" : ""}
            onClick={() => setActiveSection(s.key)}
          >
            {s.label}
          </button>
        ))}
      </nav>

      {secondInnings && (
        <div className="scorecard-innings-selector">
          {startedInnings.slice(0, 2).map((inning, index) => (
            <InningsCard
              key={`${inning.battingTeam}-${index}`}
              inning={inning}
              state={state}
              index={index}
              selected={activeInningsIndex === index}
              onClick={() => setSelectedInningsIndex(index)}
            />
          ))}
        </div>
      )}

      {activeSection === "scorecard" && (
        <>
          <section className="scorecard-panel-heading">
            <h1>{teamName(state, inn.battingTeam)} Innings</h1>
            <strong>{scoreText(inn)} <small>({ballsToOvers(inn.totalBalls, bpo)} overs)</small></strong>
          </section>

          <section className="scorecard-table-card">
            <div className="scorecard-table-title">Batting</div>
            <div className="scorecard-table-wrap">
              <table className="scorecard-table scorecard-batting-table">
                <thead><tr><th>#</th><th>Batter</th><th>R</th><th>B</th><th>4s</th><th>6s</th><th>SR</th></tr></thead>
                <tbody>
                  {batting.map((player, index) => (
                    <tr key={player.id} className={player.isStriker ? "scorecard-current-batter" : ""}>
                      <td>{index + 1}</td>
                      <td><span className="scorecard-batter-name">{player.isStriker && <b className="scorecard-striker" aria-label="striker">•</b>}{formatPlayerName(player.name)} {player.isCaptain && <em>(C)</em>} {player.isKeeper && <em>(WK)</em>}</span><small className={player.isOut ? "scorecard-dismissed" : "scorecard-not-out"}>{player.statusText}{player.isNonStriker ? " · at crease" : ""}</small></td>
                      <td>{player.balls ? player.runs : "—"}</td><td>{player.balls ? player.balls : "—"}</td><td>{player.balls ? player.fours : "—"}</td><td>{player.balls ? player.sixes : "—"}</td><td>{player.strikeRate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {yetToBat.length > 0 && (
            <section className="scorecard-yet-to-bat">
              <div className="scorecard-yet-icon" aria-hidden="true">••</div>
              <div><h2>Yet to Bat</h2><p>{yetToBat.map((player) => formatPlayerName(player.name)).join(" · ")}</p></div>
            </section>
          )}

          <section className="scorecard-table-card scorecard-bowling-card">
            <div className="scorecard-table-title"><span className="scorecard-ball-icon" aria-hidden="true">◉</span> Bowling</div>
            <div className="scorecard-table-wrap">
              <table className="scorecard-table scorecard-bowling-table">
                <thead><tr><th>#</th><th>Bowler</th><th>O</th><th>R</th><th>W</th><th>Wd</th><th>Nb</th><th>Econ</th></tr></thead>
                <tbody>
                  {bowling.length ? bowling.map((row, index) => <tr key={row.id}><td>{index + 1}</td><td>{formatPlayerName(row.name)} {row.isCaptain && <em>(C)</em>} {row.isKeeper && <em>(WK)</em>}</td><td>{row.oversText}</td><td>{row.runs}</td><td>{row.wickets}</td><td>{row.wides}</td><td>{row.noballs}</td><td>{row.econ}</td></tr>) : <tr><td colSpan={8} className="scorecard-no-data">No bowling yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section className="scorecard-summary-strip">
            <div><span>Extras</span><strong>{extras}</strong><small>({extrasDetail})</small></div>
            <div><span>Total</span><strong>{scoreText(inn)}</strong><small>({ballsToOvers(inn.totalBalls, bpo)} overs)</small></div>
            <div><span>CRR</span><strong>{crr.toFixed(2)}</strong></div>
            {secondInnings && <div><span>RRR</span><strong>{rrr === null ? "—" : rrr.toFixed(2)}</strong></div>}
          </section>
        </>
      )}

      {activeSection === "commentary" && (
        <CommentarySection state={state} />
      )}

      {activeSection === "overs" && (
        <OversSection state={state} inn={inn} />
      )}

      {activeSection === "partnerships" && (
        <PartnershipsSection state={state} inn={inn} />
      )}

      {activeSection === "graphs" && (
        <GraphsSection state={state} />
      )}
    </div>
  );
}
